'use client';
import cn from '@utils/cn';
import React, { useEffect, useRef, useState } from 'react';

type Props = {
  items?: React.ReactElement[];
  speed?: number | null;
  isReversed?: boolean;
  gap?: 4 | 8;
  containerClassName?: string;
  itemsClassName?: string;
};

export default function Slider({ items, speed, isReversed, gap = 8, containerClassName, itemsClassName }: Props) {
  const [scale, setScale] = useState(1);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function updateItems() {
      if (!containerRef.current) {
        return;
      }

      const pageWidth = window.innerWidth;
      const containerWidth = containerRef.current.offsetWidth;

      setScale((value) => {
        const itemsWidth = containerWidth / value;
        // Multiply by 2 because animation shows only half the content at any time
        // The visible half (repeatedItems) must fully cover the viewport
        const newScale = Math.ceil((pageWidth * 2) / itemsWidth);
        return newScale;
      });
    }
    updateItems();

    window.addEventListener('resize', updateItems);

    return () => {
      window.removeEventListener('resize', updateItems);
    };
  }, []);

  if (!items?.length) {
    return;
  }

  const adjustedSpeed = (speed ?? 80) * scale;
  const repeatedItems = getRepeatedItems(items, scale);
  const marqueeItems = [
    ...repeatedItems,
    ...repeatedItems.map((item, idx) => React.cloneElement(item, { key: `${item.key ?? idx}-dup` })),
  ];

  return (
    <div className="overflow-hidden">
      <div className={cn('relative w-full', containerClassName)}>
        <div
          ref={containerRef}
          className={cn(
            'flex w-max flex-nowrap items-center animate-marquee',
            {
              'gap-8 pr-8': gap === 8,
              'gap-4 pr-4': gap === 4,
              'direction-reverse pl-4 pr-0': isReversed && gap === 4,
              'direction-reverse pl-8 pr-0': isReversed && gap === 8,
            },
            itemsClassName,
          )}
          style={
            {
              '--marquee-duration': `${adjustedSpeed}s`,
              animationDirection: isReversed ? 'reverse' : 'normal',
              willChange: 'transform',
            } as React.CSSProperties
          }
        >
          {marqueeItems}
        </div>
      </div>
    </div>
  );
}

function getRepeatedItems(items: React.ReactElement[], scale: number) {
  return Array.from({ length: Math.ceil(scale) }, (_, i) =>
    items.map((item, j) => React.cloneElement(item, { key: `${item.key ?? j}-rep-${i}` })),
  ).flat();
}
